import React from 'react';
import { Box, Chip, TextField, Typography, keyframes } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { WELCOME_LINES } from '../constants';
import type { PromptStepProps } from '../types';

// Step: Prompt
const SHIMMER = keyframes`
  to { background-position: -250% 0; }
`;

const AURORA = keyframes`
  0%, 100% { transform: translateX(-50%) scale(1); opacity: 0.85; }
  50%      { transform: translateX(-50%) scale(1.12); opacity: 1; }
`;

const PromptStep: React.FC<PromptStepProps> = ({
  prompt,
  setPrompt,
  handleSendPrompt,
  promptChips,
  handleChipClick,
}) => {
  // Lazy initialiser so the greeting is fixed for the life of the screen -   // re-rolling on every keystroke would be a flicker, not a welcome.
  const [welcome] = React.useState(
    () => WELCOME_LINES[Math.floor(Math.random() * WELCOME_LINES.length)]
  );

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: 'calc(100vh - 220px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: 5,
        overflow: 'hidden',
        // Ambient aurora behind everything - pure CSS, no canvas, no lib.
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '8%',
          left: '50%',
          width: 620,
          height: 620,
          transform: 'translateX(-50%)',
          background:
            'radial-gradient(closest-side, rgba(28,157,234,0.22), transparent 70%),' +
            'radial-gradient(closest-side at 70% 40%, rgba(147,51,234,0.16), transparent 70%)',
          filter: 'blur(10px)',
          animation: `${AURORA} 12s ease-in-out infinite`,
          pointerEvents: 'none',
        },
        '& > *': { position: 'relative', zIndex: 1 },
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.5,
          py: 0.5,
          mb: 2.5,
          borderRadius: '999px',
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: '#1c9dea',
          border: '1px solid rgba(28,157,234,0.35)',
          backgroundColor: 'rgba(28,157,234,0.07)',
        }}
      >
        <AutoAwesomeIcon sx={{ fontSize: 13 }} />
        AI campaign planner
      </Box>

      <Typography
        sx={{
          fontSize: { xs: 28, md: 38 },
          fontWeight: 800,
          letterSpacing: '-0.03em',
          mb: 1.25,
          fontFamily: 'inherit',
          background:
            'linear-gradient(92deg,#1c9dea 0%,#6d5efc 45%,#c33bd4 75%,#1c9dea 100%)',
          backgroundSize: '250% 100%',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          animation: `${SHIMMER} 7s linear infinite`,
        }}
      >
        {welcome}
      </Typography>

      {/* Prompt box — gradient hairline border via a padded wrapper */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 620,
          position: 'relative',
          p: '1.5px',
          borderRadius: '18px',
          background: 'linear-gradient(120deg,#1c9dea,#6d5efc,#c33bd4)',
          boxShadow: '0 10px 40px -12px rgba(28,157,234,0.45)',
          transition: 'box-shadow .25s, transform .25s',
          '&:focus-within': {
            boxShadow: '0 14px 50px -10px rgba(109,94,252,0.55)',
            transform: 'translateY(-1px)',
          },
        }}
      >
        <TextField
          fullWidth
          multiline
          minRows={3}
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
          placeholder="e.g. Retargeting for Hiveclothing — users who saw our summer collection, ₹8L budget, 30 days, heavier on CTV, Mumbai and Delhi only…"
          variant="outlined"
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSendPrompt();
            }
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '16.5px',
              backgroundColor: '#ffffff',
              fontSize: 14,
              lineHeight: 1.6,
              color: '#1a1a1a',
              pr: '56px',
              '& fieldset': { border: 'none' },
            },
            '& .MuiInputBase-input::placeholder': {
              color: '#aab8d4',
              opacity: 1,
            },
          }}
        />

        {/* Send button */}
        <Box
          component="button"
          onClick={handleSendPrompt}
          sx={{
            position: 'absolute',
            bottom: 12,
            right: 12,
            width: 36,
            height: 36,
            borderRadius: '11px',
            background: 'linear-gradient(135deg,#1c9dea,#6d5efc)',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            transition: 'transform .15s, box-shadow .15s',
            '&:hover': {
              transform: 'scale(1.08)',
              boxShadow: '0 6px 18px -4px rgba(109,94,252,0.7)',
            },
            '&:active': { transform: 'scale(0.96)' },
          }}
        >
          <ArrowUpwardIcon sx={{ fontSize: 18 }} />
        </Box>
      </Box>

      {/* Hint chips */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          justifyContent: 'center',
          mt: 2.5,
          maxWidth: 620,
        }}
      >
        {promptChips.map(chip => (
          <Chip
            key={chip}
            label={chip}
            onClick={() => handleChipClick(chip)}
            sx={{
              fontSize: 11.5,
              fontWeight: 500,
              color: '#4b5563',
              backgroundColor: 'rgba(255,255,255,0.75)',
              backdropFilter: 'blur(6px)',
              border: '1px solid #dfe5f1',
              borderRadius: '999px',
              height: 30,
              cursor: 'pointer',
              fontFamily: 'inherit',
              transition: 'transform .15s, border-color .15s, color .15s',
              '&:hover': {
                transform: 'translateY(-2px)',
                color: '#1c9dea',
                borderColor: '#1c9dea',
                backgroundColor: '#fff',
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
};
export default PromptStep;
